import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { FormattedMessage, useIntl } from 'react-intl';
import TagManager from 'react-gtm-module';
import isEmail from 'validator/lib/isEmail';
import { AppBar, Container, Box, Button, CssBaseline, TextField, Grid, Typography } from '@mui/material';
import * as CryptoJs from 'crypto-js';
import Spinner from './Spinner';
import { Context as IntlContext } from '../context/IntlContext';
import { AppVersionContext } from '../context/AppVersionContext';
import * as FitterHelper from '../library/FitterHelper';
import * as TrackManHelper from '../library/TrackManHelper';
import * as TutorialHelper from '../library/TutorialHelper';
import * as SimulatorHelper from '../library/SimulatorHelper';
import * as VersionChecker from '../library/VersionChecker';
import * as IosHelper from '../library/IosHelper';
import * as WedgeOrderHelper from '../library/WedgeOrderHelper';
import * as CookieBotHelper from '../library/CookieBotHelper';
import background_image from '../images/vwf_background.jpg';
import vokey_design from '../images/Vokey-Design-White.png';

export default function Auth() {
    const { appVersion } = useContext(AppVersionContext);
    const navigate = useNavigate();
    const location = useLocation();
    const intl = useIntl();
    const intlContext = useContext(IntlContext);
    const [email, setEmail] = useState('');
    const [password, setPassword] = React.useState("");
    const [showSpinner, setShowSpinner] = useState(false);
    const [showAuthError, setShowAuthError] = useState(false);
    const [authError, setAuthError] = useState('');
    const [versionClicks, setVersionClicks] = useState(0);
    const [isStandalone, setIsStandalone] = useState(false);
    const [failedLoginCounter, setFailedLoginCounter] = useState(0);
    const [showExpiredLoginError, setShowExpiredLoginError] = useState(false);
    const [emailIsValid, setEmailIsValid] = useState(true);
    const [iOS, setIOS] = useState(false);
    const showForm = true;

    useEffect(() => {
        // iOS auto login handler
        (window as any).iOSLoginBridgeResponse = iOSLoginBridgeResponse;
        // iOS manual login handler
        (window as any).iOSManualLoginBridgeResponse = iOSManualLoginBridgeResponse;

        const asyncProcess = async () => {            
            try {
                //register Google Analytics
                const tagManagerArgs = {
                    gtmId: process.env.REACT_APP_GOOGLE_TAG_MANAGER || ""
                }
                TagManager.initialize(tagManagerArgs);
    
                // Update language to 'selectedLocale' if provided, else keep the current language.
                const selectedLocale = (location?.state as any)?.selectedLocale;
                if (selectedLocale) intlContext.selectLocale(selectedLocale);
                //reset the training mode in case the previous user had quit pre-maturely
                await TutorialHelper.disable();
                // make sure there is a device id on this computer
                let tmConfiguration = await TrackManHelper.getConfiguration();
                let simulatorConfiguration = await SimulatorHelper.getConfiguration();
                if (tmConfiguration.deviceId === "") {
                    tmConfiguration.deviceId = (window as any).crypto.randomUUID().substring(14, 18); //random substring from GUID
                    await TrackManHelper.saveConfiguration(tmConfiguration);
                    simulatorConfiguration.deviceId = (window as any).crypto.randomUUID().substring(14, 18); //random substring from GUID
                    await SimulatorHelper.saveConfiguration(simulatorConfiguration);
                }
            } catch (error) {
                console.error("Error setting up environment context: ", error);
            }

            try {
                // If iOSLoginBridgeRequest is not undefined, we sign in the user and redirect them to the Golfer Registration page (iOSLoginBridgeResponse).
                (window as any).webkit.messageHandlers.iOSLoginBridgeRequest.postMessage(true);
            } catch {
                // In Standalone mode show the banner by default
                handleCookiebotConsent(true);
                // If coming from the Club PWA, check for the encrypted credentials in the URL to auto login.
                try {
                    if (location.search && location.search !== "") {
                        await handlePWAAutoLogin(location.search);
                    }
                }
                catch {}
                // If iOSLoginBridgeRequest is undefined, this is the Standalone PWA flow and we render the Fitter Login page.
                setIsStandalone(true);
                // Logout any current fitter from the session
                await FitterHelper.logout();
                if ((location.state as any)?.isForcedFitterLogin) {
                    // Clear state {isForcedFitterLogin} passed by the Refresh Token error or expired login
                    navigate(location.pathname, { replace: true });
                    setShowExpiredLoginError(true);
                }
            }
        }

        asyncProcess();
    }, [location]);

    /* Poll for Cookiebot banner and show or accept the cookies, by default accept them */
    const handleCookiebotConsent = async (cookieConsent: boolean) => {
        try {
            await CookieBotHelper.pollCookiebotBanner();

            if (cookieConsent) {
                CookieBotHelper.showCookieBanner();
            } else {
                CookieBotHelper.acceptEssentialCookies();
            }
        } catch {
            CookieBotHelper.acceptEssentialCookies();
        }
    }

    /*
        Handles iOS auto login via iOSLoginBridgeRequest after SFHG app login.  
        Accepts userEmail and password if a glofer profile exists.  
        Supports fitting-to-order or standalone flow.  
        On error, redirects to iOS via iOSCloseView.
    */
    const iOSLoginBridgeResponse = async (userEmail: string, password: string, cookieConsent: boolean) => {
        try {
            handleCookiebotConsent(cookieConsent);
            await IosHelper.set(true);
            const fitter = await FitterHelper.signIn(userEmail, password, true);
            if (window.navigator.onLine) {
                const tmConfiguration = await TrackManHelper.getConfiguration();
                const simulator = await SimulatorHelper.getSimulator();
                await WedgeOrderHelper.placeAllQueuedOrders(0, tmConfiguration, simulator);
                TagManager.dataLayer({
                    dataLayer: {
                        event: 'vwf_login',
                        fitter_id: fitter.fitterID, //12006
                        fitter_type: fitter.groups,
                        country: fitter.country,
                        loc_name: fitter.locationName,
                        user_pool: fitter.userPool,
                        login_type: "auto",
                        login_source: "iOS",
                    }
                })
            }

            navigate("/apps/wedgefitting/golferinfo");
        } catch (error) {
            console.error("Error while logging in with iOSLogin credentials", error);

            TagManager.dataLayer({
                dataLayer: {
                    event: 'log_error',
                    description: 'Error while logging in with iOSLogin credentials',
                    msg: error.msg || JSON.stringify(error)
                }
            });

            // If the auto login fails, we send the fitter back to SFHG
            const iOSErrorMessage = `Error: Params=${JSON.stringify({isStandalone})} Message=${error}`;
            (window as any).webkit.messageHandlers.iOSCloseView.postMessage(iOSErrorMessage);
        }
    }

    /*
        Handles manual login from iOS guest flow.  
        On error, redirects to iOS via iOSCloseView.
    */
    const iOSManualLoginBridgeResponse = async (isStandalone: boolean = true, cookieConsent: boolean) => {
        try {
            handleCookiebotConsent(cookieConsent);
            await IosHelper.set(true);
            setIOS(true);
            setIsStandalone(isStandalone);
        } catch (error) {
            console.error("Error while enabling manual login in iOS", error);

            TagManager.dataLayer({
                dataLayer: {
                    event: 'log_error',
                    description: 'Error while enabling manual login in iOS',
                    msg: error.msg || JSON.stringify(error)
                }
            });

            // If the manual login fails, we send the fitter back to SFHG
            const iOSErrorMessage = `Error: Params=${JSON.stringify({isStandalone})} Message=${error}`;
            (window as any).webkit.messageHandlers.iOSCloseView.postMessage(iOSErrorMessage);
        }
    }

    const handlePWAAutoLogin = async (locationSearch: string | null) => {
        const encryptedCredentials = locationSearch?.substring(1); // Removing the "?" starting URL character
        const encryptedParams = encryptedCredentials?.split("&");
        if (encryptedParams && encryptedParams?.length === 3) {
            try {
                const [encryptedUserEmail, enryptedPassword, encryptedTimestamp] = encryptedParams;
                const userEmail = CryptoJs.AES.decrypt(encryptedUserEmail, process.env.REACT_APP_KEY_CRYPTO || "").toString(CryptoJs.enc.Utf8);
                const password = CryptoJs.AES.decrypt(enryptedPassword, process.env.REACT_APP_KEY_CRYPTO || "").toString(CryptoJs.enc.Utf8);
                const timestamp = CryptoJs.AES.decrypt(encryptedTimestamp, process.env.REACT_APP_KEY_CRYPTO || "").toString(CryptoJs.enc.Utf8);

                // Check against the timestamp of the link, if today is equal or more than two days ahead, its expired.
                if (new Date() < new Date(timestamp)) {
                    const fitter = await FitterHelper.signIn(userEmail, password, false);
                    if (window.navigator.onLine) {
                        const tmConfiguration = await TrackManHelper.getConfiguration();
                        const simulator = await SimulatorHelper.getSimulator();
                        await WedgeOrderHelper.placeAllQueuedOrders(0, tmConfiguration, simulator);
                        TagManager.dataLayer({
                            dataLayer: {
                                event: 'vwf_login',
                                fitter_id: fitter.fitterID, //12006
                                fitter_type: fitter.groups,
                                country: fitter.country,
                                loc_name: fitter.locationName,
                                user_pool: fitter.userPool,
                                login_type: "auto",
                                login_source: "WF-PWA",
                            }
                        })
                    }
    
                    navigate("/apps/wedgefitting/golferinfo");
                }
            } catch (error) {
                console.error("Error while logging in with PWA credentials", error);
                throw error;
            }
        }
    }

    const styles = {
        bi: {
            backgroundImage: `url(${background_image})`,
            width: "100%",
            height: "100%",
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center center"
        }
    }

    async function handleSubmit(event: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();
        const newEmailIsValid = email !== "" && isEmail(email.trim());
        setEmailIsValid(newEmailIsValid);
        if (newEmailIsValid) {
            await login(email.trim(), password.trim(), false);
        }
    }

    //iOS flag saves if they are logging in automatically via iOS app
    async function login(email: string, password: string, iOS: boolean) {
        setShowSpinner(true);
        setShowAuthError(false);
        //Authenticate the fitter. If login isn't successful, an error is thrown
        try {
            //before signing in, we need to save flag as to whether the fitter has logged on in the past. If not, we'll show the tutorial to them.
            let firstTimeLoggedIn = await FitterHelper.getFitterByEmail(email) === undefined;

            //authenticate the fitter
            const cognitoUserSession = await FitterHelper.signIn(email, password, iOS);
            intlContext.selectLocale(cognitoUserSession.locale);

            //if this is the fitters first login, save the event
            let eventName = firstTimeLoggedIn ? "vwf_first_login" : "vwf_login";
            setGtmLogin(eventName, cognitoUserSession);

            setShowSpinner(false);
            if (firstTimeLoggedIn) {
                await TutorialHelper.enable();
            }

            submitPage();
        } 
        catch (err) {
            TagManager.dataLayer({
                dataLayer: {
                    event: 'log_error',
                    description: 'Authentication - Login',
                    msg: err
                }
            });
            setFailedLoginCounter((value) => value + 1);
            // Checking for 4 instead of 5 since React doesn't immediatly update the state
            if (failedLoginCounter >= 4) {
                setTimeout(() => {
                    setShowAuthError(false);
                    setFailedLoginCounter(0);
                }, 5000)
            }

            //bad login
            setShowSpinner(false);
            setShowAuthError(true);

            if (err.code === "NotAuthorizedException" || err.message === "Incorrect username or password.") {
                setAuthError(intl.formatMessage({ id: "general.invalid.credentials" }))
            } else if (err.message === "Unverified Email") {
                setAuthError(intl.formatMessage({ id: "general.invalid.unverifiedEmail" }));
            } else if (err.code === "NetworkError" || err.message === "NetworkError") {
                setAuthError(intl.formatMessage({ id: "general.no.connection" }));
            } else {
                setAuthError(intl.formatMessage({ id: "general.invalid.credentials" }))
            }
        }
    }

    interface ICognitoUserSession {
        fitterID: number;
        groups: string;
        country: string;
        locationName: string;
    }
    const setGtmLogin = (eventName: string, cognitoUserSession: ICognitoUserSession) => {
        TagManager.dataLayer({
            dataLayer: {
                event: eventName || 'vwf_login',
                fitter_id: cognitoUserSession.fitterID, //12006
                fitter_type: cognitoUserSession.groups,
                country: cognitoUserSession.country,
                loc_name: cognitoUserSession.locationName,
                login_type: "manual",
                login_source: "WF-PWA",
            }
        })
    }

    //if app is online, check if new version is needed
    const submitPage = async () => {
        if (window.navigator.onLine && !iOS) {
            let needNewVersion = await VersionChecker.needNewVersion();
            if (!needNewVersion) {
                navigate('/apps/wedgefitting/golferinfo');
            } else {
                let versionInfo = new VersionChecker.VersionInfo(new Date(Date.now() - (3600 * 1000 * 24)), 0, "/apps/wedgefitting/golferinfo");
                await VersionChecker.setVersionInfo(versionInfo);
                navigate('/apps/wedgefitting/refreshversion');
            }
        } else {
            navigate("/apps/wedgefitting/golferinfo")
        }
    }

    const handleShowVersion = () => {
        setVersionClicks(versionClicks + 1);
    }

    const handleCreateAccount = () => {
        navigate('/apps/wedgefitting/createaccount');
    }

    const handleForgotPassword = () => {
        navigate('/apps/wedgefitting/forgotpassword');
    }

    if (!isStandalone) return (
        <div style={{ margin: "auto", marginTop: "50vh" }}>
            <Spinner />
            <div style={{position: "absolute", bottom: "25px", left: "50%", transform: "translate(-50%)", fontSize: "small"}}>{appVersion}</div>
        </div>
    )

    return (
        <>
            <div className='login-form clear-navbar' style={styles.bi}>
                {iOS && (
                    <Button
                        disableRipple
                        type="submit"
                        variant="contained"
                        sx={{
                            position: "absolute",
                            top: "25px",
                            left: "25px",
                            backgroundColor: "white",
                            color: "black",
                            borderRadius: "15px",
                            padding: "10px 20px",
                            textTransform: "initial",
                            ":hover": { backgroundColor: "white" }
                        }}
                        onClick={() => (window as any).webkit.messageHandlers.iOSCloseView.postMessage(true)}
                    >
                        <Typography>Back</Typography>
                    </Button>
                )}
                <Container component="main" maxWidth="lg">
                    <Box sx={{ flexGrow: 1 }} className='navbar'>
                        <AppBar position="static" className='appbar'>
                            <Grid container>
                                <Grid item sm={4}></Grid>
                                <Grid item sm={4}>
                                    <img style={{ paddingTop: '30px', width: '300px', alignContent: 'center' }} src={vokey_design} onClick={handleShowVersion} />
                                </Grid>
                                <Grid item sm={4} sx={{ paddingTop: '30px' }}></Grid>
                            </Grid>
                        </AppBar>
                    </Box>
                </Container>

                <Container component="main" maxWidth="sm" className='container'>
                    <CssBaseline />
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                        }}
                    >
                        <Typography component="h1" variant="h5" className='header' sx={{ textShadow: "1px 1px black" }}>
                            <FormattedMessage id="general.appname" />
                        </Typography>

                        {showForm && (
                            <>
                                <Typography component="h1" className='subheader'>
                                    <FormattedMessage id="general.login" />
                                </Typography>
                                <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1, width: '300px' }}>
                                    <Typography className='label'>
                                        <FormattedMessage id="general.email" />
                                    </Typography>
                                    <TextField
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="email"
                                        name="email"
                                        autoComplete="email"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                    />
                                    <Typography className='label'>
                                        <FormattedMessage id="general.password" />
                                    </Typography>
                                    <TextField
                                        margin="normal"
                                        required
                                        fullWidth
                                        name="password"
                                        type="password"
                                        id="password"
                                        autoComplete="current-password"
                                        className="password"
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                    />
                                    <Button
                                        disableRipple
                                        className='sign-in'
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        sx={{ mt: 3, mb: 2, fontSize: '20px' }}
                                        onClick={handleSubmit}
                                    >
                                        <FormattedMessage id="general.sign.in" />
                                    </Button>
                                        <span className="sk-wave" style={{ display: 'inline-flex', marginRight: '0px', marginTop: '20px' }} >
                                            {showSpinner && <>
                                                <span className="sk-wave-rect"></span>
                                                <span className="sk-wave-rect"></span>
                                                <span className="sk-wave-rect"></span>
                                                <span className="sk-wave-rect"></span>
                                                <span className="sk-wave-rect"></span>
                                            </>
                                            }
                                        </span>
                                    {!emailIsValid && (
                                        <Typography className='label' sx={{ color: 'white !important', marginBottom: '15px', fontSize: '22px', textShadow: "1px 1px black", textAlign: "center !important" }}>
                                            {intl.formatMessage({ id: "general.email.invalid" })}
                                        </Typography>
                                    )}
                                    {(showAuthError && failedLoginCounter < 5) &&
                                        <Typography className='label' sx={{ color: 'white !important', marginBottom: '15px', fontSize: '22px', textShadow: "1px 1px black", textAlign: "center !important" }}>
                                            {authError}
                                        </Typography>
                                    }
                                    {failedLoginCounter >= 5 && (
                                        <Typography className='label' sx={{ color: 'white !important', marginBottom: '15px', fontSize: '22px', textShadow: "1px 1px black", textAlign: "center !important" }}>
                                            <FormattedMessage id="login.failed.attempts" />
                                        </Typography>
                                    )}
                                    {showExpiredLoginError &&
                                        <Typography className='label' sx={{ color: 'white !important', marginBottom: '15px', fontSize: '22px', textShadow: "1px 1px black", textAlign: "center !important" }}>
                                            <FormattedMessage id="login.expired.1" />
                                            <br />
                                            <FormattedMessage id="login.expired.2" />
                                        </Typography>
                                    }
                                    <Grid container>
                                        <Grid item xs={6} sx={{ color: 'white', textShadow: "1px 1px black", cursor: 'pointer' }} onClick={handleCreateAccount}><FormattedMessage id="general.create.account" /></Grid>
                                        <Grid item xs={6} sx={{ color: 'white', textShadow: "1px 1px black", cursor: 'pointer' }} onClick={handleForgotPassword}><FormattedMessage id="general.forgot.password" /></Grid>
                                    </Grid>
                                </Box>
                            </>
                        )}
                    </Box>
                    {versionClicks >= 2 &&
                        <Grid container className='row' sx={{ marginTop: "20px" }}>
                            <Grid item xs={12}>
                                <Typography sx={{ color: 'white', fontSize: 'large' }}>{appVersion}</Typography>
                            </Grid>
                        </Grid>
                    }
                </Container>

                {!showForm && <>
                    <Container maxWidth="lg" sx={{ marginTop: ['150px', '250px', '450px', '550px'] }}>
                        <Grid container>
                            <Grid item xs={11} sx={{ textAlign: "right !important" }}>
                                <Typography component="h1" className='subheader' sx={{ textAlign: "right !important", textShadow: '1px 1px black' }}>
                                    <FormattedMessage id="general.start" />
                                </Typography>
                            </Grid>
                        </Grid>
                    </Container>
                </>}
            </div>
            {/* <script id="CookieDeclaration" src="https://consent.cookiebot.com/3574682d-8aec-401d-b6b3-643452bd827f/cd.js" type="text/javascript" async></script> */}
        </>
    )
}
